/**
 * Pure utility functions for mobile keyboard input handling.
 *
 * Mobile browsers (especially Android Chrome with Gboard / Samsung Keyboard)
 * don't reliably fire `beforeinput` events or honour `preventDefault()`.
 * We use a sentinel-based strategy: the hidden input always contains a
 * zero-width space (SENTINEL). After each keystroke we compare the input
 * value against the sentinel to determine what happened.
 */

/** Zero-width space used as a sentinel value in the hidden input */
export const SENTINEL = "\u200B"

/**
 * Actions that can result from interpreting the hidden input's value.
 *
 * - `{ type: "letter", letter: string }` — user typed a letter (always uppercase)
 * - `{ type: "backspace" }` — user pressed backspace / delete
 * - `{ type: "none" }` — no actionable input (value unchanged or non-letter)
 */
export type InputAction =
  | { type: "letter"; letter: string }
  | { type: "backspace" }
  | { type: "none" }

/**
 * Given the current value of the hidden input element, determines
 * what the user intended (letter input or backspace).
 *
 * @param value - The hidden input's `.value` after the browser has processed the keystroke
 * @returns The appropriate InputAction
 */
export function parseInputValue(value: string): InputAction {
  // Sentinel was deleted (or input emptied) → backspace
  if (value.length === 0 || (!value.includes(SENTINEL) && value.length < SENTINEL.length)) {
    return { type: "backspace" }
  }

  // Value unchanged → no action
  if (value === SENTINEL) {
    return { type: "none" }
  }

  // New character(s) appeared alongside or replacing the sentinel
  const stripped = value.replace(SENTINEL, "")
  if (stripped.length > 0) {
    const letter = stripped.slice(-1)
    if (/^[a-zA-Z]$/i.test(letter)) {
      return { type: "letter", letter: letter.toUpperCase() }
    }
  }

  // Non-letter character typed (number, symbol, etc.) → ignore
  return { type: "none" }
}

/**
 * Maps a click/tap's client coordinates to a grid cell position,
 * using the SVG's bounding rect and viewBox dimensions.
 *
 * @returns `[row, col]` if the tap hits a valid cell, or `null` otherwise
 */
export function tapToCell(
  clientX: number,
  clientY: number,
  svgRect: { left: number; top: number; width: number },
  svgSize: number,
  cellSize: number,
  padding: number,
  gridSize: number,
): [number, number] | null {
  const x = clientX - svgRect.left
  const y = clientY - svgRect.top

  const scale = svgSize / svgRect.width
  const svgX = x * scale - padding
  const svgY = y * scale - padding

  const col = Math.floor(svgX / cellSize)
  const row = Math.floor(svgY / cellSize)

  if (row >= 0 && row < gridSize && col >= 0 && col < gridSize) {
    return [row, col]
  }
  return null
}
